import React from 'react';
import PropTypes from 'prop-types';
import { formatBigNumber } from '../../../SleepHub/sleepHubApi';
import {
  orDash,
  usePlanType,
  TrendPanel,
  StartsPanel,
  WeekdayPanel,
  SplitPanel,
  CompletionNote,
  GuidedPathFootnote
} from './GuidedPathShell';
import { StatCard, Panel, STAT_ICONS } from '../../../../component/common/designSystem';
import { ACTIVITY_RAMP, TENURE_RAMP, MINUTE_RAMP, planColor } from './guidedPathTheme';

/**
 * One kind of plan's own page — the AI plan or the custom plan.
 *
 * Both kinds render this component, because the question each answers is
 * identical and the two are stored in one collection with one boolean between
 * them. The page is deliberately the same shape for both so that flipping tabs
 * compares like with like.
 *
 * TWO COUNTS THAT DO NOT RECONCILE SIT ON THIS PAGE. "Days engaged" counts days
 * where somebody ticked at least one ritual; "days marked done" counts days
 * flagged complete. The app can set the second without the first — the
 * "complete all for today" action flips the day flag without touching the
 * rituals — so either can exceed the other. They are shown as two separate
 * facts and never divided into one another.
 */
const PlanTypePage = ({ fetcher, populationLabel = 'users' }) => {
  const [stats, fallback] = usePlanType(fetcher);
  if (fallback) return fallback;

  const color = planColor(stats.key);

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People With One"
          value={formatBigNumber(stats.people || 0)}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={stats.totalUsers > 0 ? `${stats.takeUp}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Still Running"
          value={formatBigNumber(stats.running || 0)}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Follow-through"
          value={stats.ritualsOffered > 0 ? `${stats.followThrough}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Rituals Ticked (30d)"
          value={formatBigNumber(stats.ritualsTicked || 0)}
        />
      </div>

      <CompletionNote />

      {/* ---------- What a plan-day looks like ---------- */}
      <div className="mt-4">
        <Panel
          title="Days inside a plan"
          subtitle="A plan produces a day of rituals; these are the ones that got touched"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard
              icon={STAT_ICONS.catalog}
              title="Days Generated"
              value={formatBigNumber(stats.dayRecords || 0)}
            />
            <StatCard
              icon={STAT_ICONS.sessions}
              title="Days Engaged"
              value={formatBigNumber(stats.daysEngaged || 0)}
            />
            {/* Counted from a different flag and never reconciled with the one
                beside it — see the header. */}
            <StatCard
              icon={STAT_ICONS.goals}
              title="Days Marked Done"
              value={formatBigNumber(stats.daysFinished || 0)}
            />
            <StatCard
              icon={STAT_ICONS.time}
              title="Rituals Per Active Day"
              value={orDash(stats.ritualsPerActiveDay)}
            />
          </div>

          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
            {stats.dayTakeUp}% of the days this kind of plan generated saw at least one ritual
            ticked. &ldquo;Days marked done&rdquo; is a separate flag the app can set without any
            ritual being ticked, so the two counts overlap rather than nest — neither is a
            percentage of the other.
          </p>
        </Panel>
      </div>

      {/* ---------- Doing the work ---------- */}
      <div className="mt-4">
        <TrendPanel trend={stats.dailyTrend} color={color} />
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StartsPanel trend={stats.startedTrend} color={color} />
        <WeekdayPanel rows={stats.weekday} />
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SplitPanel
          title="Days active per person"
          subtitle="Distinct days somebody ticked at least one ritual"
          rows={stats.activeDaySplit}
          ramp={ACTIVITY_RAMP}
          unit="people"
          empty="Nobody with this kind of plan has ticked a ritual yet."
        />
        <SplitPanel
          title="How long people have had one"
          subtitle="Measured from the day the plan was created"
          rows={stats.tenureSplit}
          ramp={TENURE_RAMP}
          unit="plans"
          empty="No plan of this kind carries a usable start date."
        />
      </div>

      {/* ---------- Coming back ---------- */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel
          title="Coming back"
          subtitle="People active last week who were also active the week before"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              icon={STAT_ICONS.users}
              title="Active Last Week"
              value={formatBigNumber(stats.returning?.activeLastWeek || 0)}
            />
            <StatCard
              icon={STAT_ICONS.streak}
              title="Came Back"
              value={formatBigNumber(stats.returning?.returned || 0)}
            />
            <StatCard
              icon={STAT_ICONS.activeUsers}
              title="Return Rate"
              value={
                stats.returning?.activeWeekBefore > 0 ? `${stats.returning.returnRate}%` : '-'
              }
            />
          </div>
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
            Measured against the {formatBigNumber(stats.returning?.activeWeekBefore || 0)}{' '}
            {stats.returning?.activeWeekBefore === 1 ? 'person' : 'people'} who were active the week
            before. With nobody active that week there is nothing to return from, so the rate is a
            dash rather than a zero.
          </p>
        </Panel>

        {/* Only the custom plan asks how much time somebody can give it; the AI
            plan never records one, so this panel is empty on that tab rather
            than inventing a default everybody appears to have chosen. */}
        <SplitPanel
          title="Time committed per day"
          subtitle="What people said they could give it"
          rows={stats.minuteSplit}
          ramp={MINUTE_RAMP}
          unit="plans"
          empty="This kind of plan does not ask how much time somebody can commit, so there is nothing to show."
        />
      </div>

      {stats.undated > 0 ? (
        <p className="mt-3 text-xs text-shoorah-secondary dark:text-gray-400">
          {stats.undated} {stats.undated === 1 ? 'plan carries' : 'plans carry'} no readable start
          date, so {stats.undated === 1 ? 'it is' : 'they are'} counted in the totals but left out
          of the tenure and start-date charts rather than dated to the epoch.
        </p>
      ) : null}

      <GuidedPathFootnote minimum={stats.minimum} />
    </div>
  );
};

PlanTypePage.propTypes = {
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string
};

export default PlanTypePage;
